import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { API_URL, cn, getInitials } from "../../lib/utils";
import { useLocation } from "react-router-dom";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";

import {
    HelpCircle,
    X,
    Send,
    Loader2,
    Sparkles,
    ChevronDown,
    RotateCcw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const HelpChatWidget = () => {
    const { t, i18n } = useTranslation();
    const { token, user } = useAuth();
    const location = useLocation();

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(true);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [isOpen]);

    // Load suggestions
    const loadSuggestions = useCallback(async () => {
        try {
            const lang = i18n.language?.substring(0, 2) || "fr";
            const res = await fetch(`${API_URL}/help/suggestions?lang=${lang}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setSuggestions(data.suggestions || []);
            }
        } catch { /* silent */ }
    }, [token, i18n.language]);

    useEffect(() => {
        if (isOpen && suggestions.length === 0) {
            loadSuggestions();
        }
    }, [isOpen, suggestions.length, loadSuggestions]);

    // Send question
    const sendQuestion = async (question) => {
        if (!question?.trim() || loading) return;

        const userMsg = { role: "user", content: question.trim() };
        setMessages(prev => [...prev, userMsg]);
        setInput("");
        setShowSuggestions(false);
        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/help/ask`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    question: question.trim(),
                    session_id: sessionId,
                    context: {
                        page: location.pathname,
                        role: user?.role,
                        plan: user?.plan,
                    },
                }),
            });

            if (!res.ok) throw new Error();
            const data = await res.json();

            setMessages(prev => [...prev, { role: "assistant", content: data.answer }]);
            if (!sessionId) setSessionId(data.session_id);
        } catch {
            setMessages(prev => [
                ...prev,
                {
                    role: "assistant",
                    content: "Désolé, une erreur est survenue. Veuillez réessayer.",
                    isError: true,
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    // New session
    const resetSession = () => {
        setMessages([]);
        setSessionId(null);
        setShowSuggestions(true);
    };

    if (!token) return null;

    return (
        <>
            {/* Floating Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center md:bottom-8 md:right-8"
                    data-testid="help-center-btn"
                >
                    <HelpCircle className="w-6 h-6" />
                </button>
            )}

            {/* Chat Panel */}
            {isOpen && (
                <div
                    className="fixed bottom-0 right-0 z-[60] w-full h-full sm:bottom-4 sm:right-4 sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] flex flex-col bg-background border border-border rounded-none sm:rounded-2xl shadow-2xl overflow-hidden"
                    data-testid="help-center-panel"
                >
                    {/* Header */}
                    <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-sm">Centre d'aide Orkestria</h3>
                                <p className="text-[11px] opacity-80">Assistant IA</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {messages.length > 0 && (
                                <button
                                    onClick={resetSession}
                                    className="p-1.5 rounded-md hover:bg-primary-foreground/20 transition-colors"
                                    title="Nouvelle conversation"
                                    data-testid="help-reset-btn"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1.5 rounded-md hover:bg-primary-foreground/20 transition-colors"
                                data-testid="help-close-btn"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                        {/* Welcome + Suggestions */}
                        {messages.length === 0 && showSuggestions && (
                            <div className="space-y-4">
                                <div className="text-center py-2">
                                    <Sparkles className="w-10 h-10 text-primary mx-auto mb-2 opacity-60" />
                                    <h4 className="font-medium text-sm">Comment puis-je vous aider ?</h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Posez-moi une question sur Orkestria
                                    </p>
                                </div>
                                {suggestions.map((cat, i) => (
                                    <div key={i}>
                                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                                            {cat.category}
                                        </p>
                                        <div className="space-y-1">
                                            {cat.questions.map((q, j) => (
                                                <button
                                                    key={j}
                                                    onClick={() => sendQuestion(q)}
                                                    className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                                                    data-testid={`suggestion-${i}-${j}`}
                                                >
                                                    {q}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Chat messages */}
                        <div className="space-y-4">
                            {messages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={cn(
                                        "flex gap-2",
                                        msg.role === "user" ? "justify-end" : "justify-start"
                                    )}
                                    data-testid={`help-message-${idx}`}
                                >
                                    {msg.role === "assistant" && (
                                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                            <Sparkles className="w-3.5 h-3.5 text-primary" />
                                        </div>
                                    )}
                                    <div
                                        className={cn(
                                            "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm",
                                            msg.role === "user"
                                                ? "bg-primary text-primary-foreground rounded-br-md"
                                                : msg.isError
                                                    ? "bg-destructive/10 text-destructive rounded-bl-md"
                                                    : "bg-muted rounded-bl-md"
                                        )}
                                    >
                                        <div className="break-words leading-relaxed help-response">
                                            {msg.role === "assistant" ? (
                                                <ReactMarkdown
                                                    components={{
                                                        p: ({children}) => <p className="my-1">{children}</p>,
                                                        strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                                                        ol: ({children}) => <ol className="list-decimal list-inside my-1.5 space-y-0.5">{children}</ol>,
                                                        ul: ({children}) => <ul className="list-disc list-inside my-1.5 space-y-0.5">{children}</ul>,
                                                        li: ({children}) => <li className="text-sm">{children}</li>,
                                                        h3: ({children}) => <h3 className="font-semibold mt-2 mb-1">{children}</h3>,
                                                        h4: ({children}) => <h4 className="font-semibold mt-1.5 mb-0.5">{children}</h4>,
                                                        a: ({href, children}) => <a href={href} className="text-primary underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                                                    }}
                                                >
                                                    {msg.content}
                                                </ReactMarkdown>
                                            ) : (
                                                msg.content
                                            )}
                                        </div>
                                    </div>
                                    {msg.role === "user" && (
                                        <Avatar className="w-7 h-7 shrink-0 mt-0.5">
                                            <AvatarFallback className="text-[10px] bg-primary/10">
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                    )}
                                </div>
                            ))}
                            {loading && (
                                <div className="flex gap-2 items-start" data-testid="help-loading">
                                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                        <Sparkles className="w-3.5 h-3.5 text-primary" />
                                    </div>
                                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                                            <span className="w-2 h-2 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                                            <span className="w-2 h-2 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </ScrollArea>

                    {/* Input */}
                    <div className="p-3 border-t border-border bg-card shrink-0">
                        <div className="flex items-end gap-2">
                            <Textarea
                                ref={inputRef}
                                placeholder="Posez votre question..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendQuestion(input);
                                    }
                                }}
                                className="min-h-[40px] max-h-24 resize-none text-sm"
                                disabled={loading}
                                data-testid="help-input"
                            />
                            <Button
                                onClick={() => sendQuestion(input)}
                                disabled={!input.trim() || loading}
                                size="icon"
                                className="shrink-0 h-[40px] w-[40px]"
                                data-testid="help-send-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground text-center mt-1.5">
                            Propulsé par GPT-5.2 · Basé sur la documentation Orkestria
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default HelpChatWidget;
